/**
 * DocumentCard Component
 * 
 * Componente para exibir informações de um documento em formato de card.
 * 
 * Funcionalidades:
 * - Exibe informações do documento
 * - Badge de expiração com cores por urgência
 * - Botões de ação (visualizar, editar, deletar)
 * - Preview de imagem
 */

import { useState } from 'react'
import {
  FileText,
  Image as ImageIcon,
  FileEdit,
  Paperclip,
  Download,
  Edit,
  Trash2,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  AlertTriangle
} from 'lucide-react'
import './DocumentCard.css'

function DocumentCard({
  document,
  onView,
  onEdit,
  onDelete,
  onDownload,
  showActions = true,
  className = ''
}) {
  const [imageError, setImageError] = useState(false)

  const isImage = document?.file_type?.startsWith('image/') || 
    ['jpg', 'jpeg', 'png', 'gif'].includes(document?.file_url?.split('.').pop()?.toLowerCase())

  const getExpiryStatus = () => {
    if (!document?.expiry_date) return null

    const expiryDate = new Date(document.expiry_date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    expiryDate.setHours(0, 0, 0, 0)

    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))

    if (daysUntilExpiry < 0) {
      return { label: 'Vencido', variant: 'expired', days: Math.abs(daysUntilExpiry) }
    } else if (daysUntilExpiry <= 7) {
      return { label: 'Vence em breve', variant: 'urgent', days: daysUntilExpiry }
    } else if (daysUntilExpiry <= 30) {
      return { label: 'Vence em breve', variant: 'warning', days: daysUntilExpiry }
    }

    return { label: 'Válido', variant: 'valid', days: daysUntilExpiry }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  const getFileIcon = (fileType, fileName) => {
    if (!fileType && !fileName) return <Paperclip size={48} />
    
    const ext = fileName?.split('.').pop()?.toLowerCase() || ''
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext) || fileType?.startsWith('image/')) {
      return <ImageIcon size={48} />
    }
    if (ext === 'pdf' || fileType === 'application/pdf') {
      return <FileText size={48} />
    }
    if (['doc', 'docx'].includes(ext) || fileType?.includes('word')) {
      return <FileEdit size={48} />
    }
    return <Paperclip size={48} />
  }

  const expiryStatus = getExpiryStatus()
  const showDownload = Boolean(document?.file_url)
  const showEdit = showActions && Boolean(onEdit)
  const showDelete = showActions && Boolean(onDelete)

  return (
    <div className={`document-card ${className}`}>
      {isImage && !imageError && document?.file_url ? (
        <div className="document-card-image">
          <img
            src={document.file_url}
            alt={document.title || 'Documento'}
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="document-card-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
          {getFileIcon(document?.file_type, document?.file_name)}
        </div>
      )}

      <div className="document-card-content">
        <div className="document-card-header">
          <h3 className="document-card-title" title={document?.title}>
            {document?.title || 'Sem título'}
          </h3>
          {expiryStatus && (
            <span className={`document-card-expiry document-card-expiry-${expiryStatus.variant}`} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {expiryStatus.variant === 'expired' && <AlertCircle size={14} />}
              {expiryStatus.variant === 'urgent' && <AlertTriangle size={14} />}
              {expiryStatus.variant === 'warning' && <AlertTriangle size={14} />}
              {expiryStatus.variant === 'valid' && <CheckCircle size={14} />}
              {expiryStatus.label}
              {expiryStatus.days !== undefined && (
                <span className="document-card-expiry-days"> ({expiryStatus.days} {expiryStatus.days === 1 ? 'dia' : 'dias'})</span>
              )}
            </span>
          )}
        </div>

        {document?.description && (
          <p className="document-card-description">{document.description}</p>
        )}

        {document?.category && (
          <span className="document-card-category">{document.category.name}</span>
        )}

        <div className="document-card-meta">
          {document?.file_name && (
            <span className="document-card-meta-item" title={document.file_name} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Paperclip size={14} />
              {document.file_name}
            </span>
          )}
          {document?.created_at && (
            <span className="document-card-meta-item" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Calendar size={14} />
              {formatDate(document.created_at)}
            </span>
          )}
          {document?.expiry_date && (
            <span className="document-card-meta-item" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Clock size={14} />
              Vence em {formatDate(document.expiry_date)}
            </span>
          )}
        </div>
      </div>

      {(showDownload || showEdit || showDelete) && (
        <div className="document-card-actions">
          {showDownload && (
            <button
              className="document-card-action document-card-action-view"
              onClick={() => (onDownload || onView)?.(document)}
              title="Baixar"
              aria-label="Baixar documento"
            >
              <Download size={18} />
            </button>
          )}
          {showEdit && (
            <button
              className="document-card-action document-card-action-edit"
              onClick={() => onEdit(document)}
              title="Editar"
              aria-label="Editar documento"
            >
              <Edit size={18} />
            </button>
          )}
          {showDelete && (
            <button
              className="document-card-action document-card-action-delete"
              onClick={() => onDelete(document)}
              title="Deletar"
              aria-label="Deletar documento"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default DocumentCard

